/**
 * Loading Screen Component
 * 
 * This component displays while the search is in progress.
 * It shows:
 * - Animated progress bar (simulated, not backend-dependent)
 * - Current status message based on progress
 * - Real-time statistics from backend polling
 * - Cancel button
 * 
 * It polls the backend API for status updates and simulates progress
 * locally since the backend doesn't return progress_percent.
 */

import React, { useState, useEffect, useRef } from 'react';
import { getJobStatus, pollJobStatus, getResults } from '../utils/api';

function LoadingScreen({ jobId, searchParams, onComplete, onError }) {
  // ═══════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════
  
  // Current progress percentage (0-100) - SIMULATED LOCALLY
  const [progress, setProgress] = useState(0);
  
  // Job status: 'pending', 'running', 'completed', 'failed'
  const [status, setStatus] = useState('pending');
  
  // Total number of leads found (from backend)
  const [totalLeads, setTotalLeads] = useState(0);
  
  // Whether the search was cancelled
  const [isCancelled, setIsCancelled] = useState(false);
  
  // Elapsed time in seconds
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // References for cleanup
  const pollIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const elapsedIntervalRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════

  useEffect(() => {
    // Start polling for backend status updates
    startPolling();
    
    // Start tracking elapsed time
    elapsedIntervalRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    
    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // SIMULATED PROGRESS EFFECT (Separate from polling)
  // ═══════════════════════════════════════════════════════════════
  
  /**
   * Simulates progress animation while server is working
   * Uses a formula that slows down as progress increases:
   * - Fast (5-30): API connection phase
   * - Medium (30-70): Search phase
   * - Slow (70-90): Processing phase
   * - Very slow (90+): Capped at 92 until completion
   */
  useEffect(() => {
    if (status !== 'running') return;
    
    progressIntervalRef.current = setInterval(() => {
      setProgress(prevProgress => {
        // Never exceed 92 until actual completion
        if (prevProgress >= 92) return 92;
        
        let increment = 0;
        
        // Increment based on current progress
        if (prevProgress < 30) {
          // Fast: 2-3% every 500ms (0-30% in ~5-8 seconds)
          increment = 2 + Math.random() * 1;
        } else if (prevProgress < 60) {
          // Medium: 1-2% every 500ms (30-60% in ~15-30 seconds)
          increment = 1 + Math.random() * 1;
        } else if (prevProgress < 85) {
          // Slow: 0.5-1% every 500ms (60-85% in ~30-50 seconds)
          increment = 0.5 + Math.random() * 0.5;
        } else {
          // Very slow: 0.2-0.5% every 500ms (85-92% in ~70+ seconds)
          increment = 0.2 + Math.random() * 0.3;
        }
        
        return Math.min(prevProgress + increment, 92);
      });
    }, 500);
    
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [status]);

  // ═══════════════════════════════════════════════════════════════
  // POLLING LOGIC (Status updates from backend)
  // ═══════════════════════════════════════════════════════════════

  const startPolling = () => {
    // Poll every 1.5 seconds for backend status updates
    pollIntervalRef.current = setInterval(async () => {
      if (isCancelled) return;
      
      try {
        const jobStatus = await getJobStatus(jobId);
        const newStatus = jobStatus.status;
        
        // Update status
        setStatus(newStatus);
        
        // Extract leads_found from response (may be called leads_found or total_leads)
        const leadsCount = jobStatus.leads_found || jobStatus.total_leads || 0;
        setTotalLeads(leadsCount);
        
        // Handle completion
        if (newStatus === 'completed') {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          
          setProgress(100);
          
          // Small delay for visual satisfaction
          setTimeout(async () => {
            try {
              const results = await getResults(jobId);
              onComplete(results);
            } catch (error) {
              console.error('Error fetching results:', error);
              onError('Failed to fetch results. Please try again.');
            }
          }, 500);
        }
        
        // Handle failure
        if (newStatus === 'failed') {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
          onError(jobStatus.error_message || 'Search failed. Please try again.');
        }
        
      } catch (error) {
        console.error('Error polling status:', error);
        // Don't stop polling on error, just log it
      }
    }, 1500);
  };

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const handleCancel = () => {
    setIsCancelled(true);
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    onError('Search cancelled by user');
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Get a friendly status message based on progress and status
   */
  const getStatusDisplay = () => {
    if (status === 'pending') return 'Preparing your search...';
    
    if (status === 'running') {
      if (progress < 15) return 'Connecting to Google Places API...';
      if (progress < 35) return 'Searching for businesses...';
      if (progress < 55) return 'Expanding search area...';
      if (progress < 75) return 'Fetching business details...';
      if (progress < 90) return 'Processing and deduplicating results...';
      return 'Almost done...';
    }
    
    if (status === 'completed') return 'Search completed!';
    if (status === 'failed') return 'Search failed';
    
    return 'Searching...';
  };
  
  /**
   * Get sub-message showing search context and elapsed time
   */
  const getSubMessage = () => {
    const elapsed = elapsedTime > 0 ? ` • Elapsed: ${elapsedTime}s` : '';
    return `Finding ${searchParams.business_type} in ${searchParams.city}${elapsed}`;
  };

  /**
   * Get color based on progress
   */
  const getProgressColor = () => {
    if (progress < 30) return 'from-blue-500 to-blue-600';
    if (progress < 60) return 'from-blue-500 to-purple-500';
    if (progress < 90) return 'from-purple-500 to-purple-600';
    return 'from-purple-500 to-pink-500';
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
          Searching for <span className="text-gradient">Leads</span>
        </h2>
        <p className="text-gray-400">
          {searchParams.business_type} in {searchParams.city}
        </p>
      </div>

      {/* Loading Card */}
      <div className="card p-8 shadow-2xl">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/logo-small.png"
            alt="LeadGen AI"
            className="h-12 w-auto object-contain"
            loading="lazy"
          />
        </div>

        {/* Animated Icon */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            {/* Outer spinning ring */}
            <div className="w-24 h-24 rounded-full border-4 border-white/10 border-t-blue-500 animate-spin" />
            {/* Inner pulsing circle */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 animate-pulse" />
            </div>
            {/* Center icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <svg 
                className="w-8 h-8 text-blue-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Status Message */}
        <div className="text-center mb-6">
          <p className="text-lg text-white font-medium mb-1">
            {getStatusDisplay()}
          </p>
          <p className="text-sm text-gray-500">{getSubMessage()}</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progress</span>
            <span className="font-semibold text-white">{Math.round(progress)}%</span>
          </div>
          <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${getProgressColor()} progress-bar-fill rounded-full transition-all duration-300`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-slate-800/50 rounded-lg p-6 text-center mb-8 border border-white/10">
          {status === 'running' ? (
            // Show spinner while running
            <div className="flex flex-col items-center justify-center">
              <div className="w-12 h-12 rounded-full border-4 border-white/10 border-t-blue-400 animate-spin mb-3" />
              <p className="text-sm text-gray-400">Discovering leads...</p>
            </div>
          ) : status === 'completed' ? (
            // Show final count when complete
            <div>
              <p className="text-4xl font-bold text-white mb-2">
                <span className="text-gradient">{totalLeads} Leads Found!</span>
              </p>
              <p className="text-gray-400 font-medium tracking-wide">Ready to explore</p>
            </div>
          ) : (
            // Show placeholder while pending/failed
            <p className="text-gray-400">Starting...</p>
          )}
        </div>

        {/* Tips */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm text-blue-300 font-medium mb-1">Did you know?</p>
              <p className="text-sm text-gray-400">
                Businesses without websites are often easier to reach and more likely 
                to need your services. Use the "No Website" filter to find these leads!
              </p>
            </div>
          </div>
        </div>

        {/* Cancel Button */}
        <button
          onClick={handleCancel}
          className="btn-secondary w-full"
        >
          Cancel Search
        </button>
      </div>

      {/* Estimated Time */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500 max-w-lg mx-auto">
          Large searches (1000+ leads) may take up to several minutes as our AI dynamically uses
          Region, Grid, and Keyword Strategies to comprehensively scan {searchParams.city}.
        </p>
      </div>
    </div>
  );
}

export default LoadingScreen;
